/**
 * The backend's messages, as they travel (docs/NEW-UI/protocollo.md), checked on the way in and turned
 * into the kiosk's own events. Only the fields the platform reads are declared; everything a domain
 * adds rides along untouched (`passthrough`) and is the domain pack's to read.
 *
 * A message that does not parse is reported and dropped: never a crash, never a half-applied state.
 */
import { z } from 'zod';

export const accessibilityModes = ['none', 'visual', 'hearing', 'motor', 'cognitive'] as const;
export type AccessibilityMode = (typeof accessibilityModes)[number];

/** An offer waiting for its answer (backend harness/offers): what the pill says, and the button its tap sends. */
export const offerSchema = z.object({
  id: z.string(),
  text: z.string().optional(),
  button: z.string().optional(),
  data: z.record(z.string(), z.unknown()).default({}),
}).passthrough();
export type Offer = z.infer<typeof offerSchema>;

export const sessionSchema = z.object({
  session_id: z.string(),
  state: z.string(),
  previous_state: z.string().optional(),
  language: z.string().default('it'),
  language_chosen: z.boolean().optional(),
  turn_count: z.number().default(0),
  page_idx: z.number().default(0),
  recovery_level: z.number().default(0),
  accessibility: z.object({
    mode: z.enum(accessibilityModes).catch('none'),
    activated_by: z.string().optional(),
  }).passthrough().default({ mode: 'none' }),
  valid_actions: z.array(z.string()).default([]),
  // a malformed offer is no offer: the screen never breaks on it
  offer: offerSchema.nullable().default(null).catch(null),
}).passthrough();

/** A session as the backend sends it: the core fields typed, the domain's fields as unknown. */
export type Session = z.infer<typeof sessionSchema> & Record<string, unknown>;

/** Every domain spec starts its flow on this screen (backend/backend/config/domain_spec.py: START_SCREEN). */
export const START_SCREEN = 'idle';

export type InputSource = 'touch' | 'text' | 'voice' | 'vision';

const turnId = z.string();

export const assistantMessageSchema = z.object({
  turn_id: turnId,
  text: z.string(),
  action: z.string().optional(),
  answered_by: z.string().optional(),
  state: z.string().optional(),
  previous_state: z.string().optional(),
  nlu_level: z.string().optional(),
  ttfb_ms: z.number().optional(),
  blocked: z.boolean().optional(),
  emoji: z.string().optional(),
  entities: z.record(z.string(), z.unknown()).optional(),
  map: z.object({ station: z.string(), level: z.string().optional(), highlight: z.array(z.string()).default([]) }).optional(),
  /** A detail of a shown item to open on its card (core/detail.ts): a train's itinerary. */
  detail: z.object({ item: z.string(), view: z.string() }).optional(),
  debug_trace: z.array(z.record(z.string(), z.unknown())).optional(),
  debug_state: z.record(z.string(), z.unknown()).optional(),
  debug_nlu: z.unknown().optional(),
  debug_nlg: z.unknown().optional(),
}).passthrough();
export type AssistantMessage = z.infer<typeof assistantMessageSchema>;

/** What the kiosk reacts to, whatever brought it (live SSE, a replay, a test). */
export type ServerEvent =
  | { kind: 'session'; session: Session }
  | { kind: 'user-said'; turnId: string; text: string; source: InputSource }
  | { kind: 'processing'; turnId: string }
  | { kind: 'understood'; turnId: string; action: string; confidence: number | null }
  | { kind: 'reply'; message: AssistantMessage }
  | { kind: 'reset' }
  | { kind: 'inactivity-warning'; message: string; countdownS: number }
  | { kind: 'turn-error'; turnId: string; message: string }
  | { kind: 'vision-error'; turnId: string; message: string }
  /** The backend took back the last voice turn: the user went on, and both parts become one turn. */
  | { kind: 'withdrawn'; text: string }
  /**
   * Words from outside the dialogue, said aloud by the kiosk: the system's (a delay) or an operator's. `wake`: they
   * wait on the backend for this kiosk to open its closed voice and say them, up to `ttlS`.
   */
  | { kind: 'announcement'; text: string; priority: 'normal' | 'critical'; source: 'system' | 'operator'; wake: boolean; ttlS: number | null }
  | { kind: 'accessibility-changed'; mode: AccessibilityMode };

export const SSE_EVENT_NAMES = [
  'state_update', 'user_message_ack', 'processing_start', 'nlu_resolved', 'assistant_message',
  'session_reset', 'inactivity_warning', 'turn_error', 'vision_error', 'turn_withdrawn', 'announcement',
] as const;

export type ParseResult = { ok: true; event: ServerEvent } | { ok: false; reason: string };

export function parseSession(data: unknown): { ok: true; session: Session } | { ok: false; reason: string } {
  const r = sessionSchema.safeParse(data);
  if (!r.success) return { ok: false, reason: summarize(r.error) };
  return { ok: true, session: r.data as Session };
}

const ackSchema = z.object({ turn_id: turnId, text: z.string(), source: z.enum(['touch', 'text', 'voice', 'vision']).catch('text') });
const nluSchema = z.object({ turn_id: turnId, action: z.string(), confidence: z.number().nullable().optional() });
const warningSchema = z.object({ message: z.string(), countdown_s: z.number() });
const errorSchema = z.object({ turn_id: turnId.default(''), message: z.string().default('') });
const announcementSchema = z.object({
  text: z.string(),
  priority: z.enum(['normal', 'critical']).catch('normal'),
  source: z.enum(['system', 'operator']).catch('system'),
  wake: z.boolean().catch(false).default(false),
  ttl_s: z.number().nullable().catch(null).default(null),
});
const accessibilitySchema = z.object({ event: z.literal('accessibility_changed'), mode: z.enum(accessibilityModes) });

/** One SSE frame (event name + JSON data, already parsed) → a kiosk event. */
export function parseServerEvent(name: string, data: unknown): ParseResult {
  switch (name) {
    case 'state_update': {
      const a11y = accessibilitySchema.safeParse(data);
      if (a11y.success) return { ok: true, event: { kind: 'accessibility-changed', mode: a11y.data.mode } };
      const s = parseSession(data);
      return s.ok ? { ok: true, event: { kind: 'session', session: s.session } } : s;
    }
    case 'user_message_ack':
      return map(ackSchema, data, (d) => ({ kind: 'user-said', turnId: d.turn_id, text: d.text, source: d.source }));
    case 'processing_start':
      return map(z.object({ turn_id: turnId }), data, (d) => ({ kind: 'processing', turnId: d.turn_id }));
    case 'nlu_resolved':
      return map(nluSchema, data, (d) => ({ kind: 'understood', turnId: d.turn_id, action: d.action, confidence: d.confidence ?? null }));
    case 'assistant_message':
      return map(assistantMessageSchema, data, (d) => ({ kind: 'reply', message: d }));
    case 'session_reset':
      return { ok: true, event: { kind: 'reset' } };
    case 'inactivity_warning':
      return map(warningSchema, data, (d) => ({ kind: 'inactivity-warning', message: d.message, countdownS: d.countdown_s }));
    case 'turn_error':
      return map(errorSchema, data, (d) => ({ kind: 'turn-error', turnId: d.turn_id, message: d.message }));
    case 'vision_error':
      return map(errorSchema, data, (d) => ({ kind: 'vision-error', turnId: d.turn_id, message: d.message }));
    case 'announcement':
      return map(announcementSchema, data, (d) => ({ kind: 'announcement', text: d.text, priority: d.priority, source: d.source, wake: d.wake, ttlS: d.ttl_s }));
    case 'turn_withdrawn':
      return map(z.object({ text: z.string().default('') }), data, (d) => ({ kind: 'withdrawn', text: d.text }));
    default:
      return { ok: false, reason: `unknown event "${name}"` };
  }
}

function map<S extends z.ZodType>(schema: S, data: unknown, build: (d: z.infer<S>) => ServerEvent): ParseResult {
  const r = schema.safeParse(data);
  return r.success ? { ok: true, event: build(r.data) } : { ok: false, reason: summarize(r.error) };
}

function summarize(error: z.ZodError): string {
  return error.issues.slice(0, 3).map((i) => `${i.path.join('.') || '(root)'}: ${i.message}`).join('; ');
}
